import {
  EmptySearchState,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@fountain/fountain-ui-components';
import { Collapse, Grid } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Choose } from 'react-extras';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Pagination } from 'components/Pagination';
import SearchInput from 'components/SearchInput';
import useRefFocus from 'hooks/useRefFocus';
import useRefMeasurements from 'hooks/useRefMeasurements';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectSaga } from 'utils/injectSaga';

import { fetchMessageTemplatesAction } from './actions';
import {
  MessageTemplatesType,
  MessageTemplatesVariant,
  REDUCER_KEY,
} from './constants';
import messages from './messages';
import RecentlyUsedTemplatesList from './RecentlyUsedTemplatesList';
import reducer from './reducer';
import saga from './saga';
import makeSelectTemplateMenuState from './selectors';
import useStyles from './styles';
import TemplatesList from './TemplatesList';

// ********************************************************************************
// == Constant ====================================================================
const SEARCH_DEBOUNCE_MS = 300;

// == Component ===================================================================
const TemplateMenuContent = ({
  intl,
  onSelected,
  templateType,
  variant,
  listWhatsAppTemplatesOnly,
  sourceAplicantId,
  whatsAppProvider,
}) => {
  const dispatch = useDispatch();

  useInjectReducer({ key: REDUCER_KEY, reducer });

  useInjectSaga({ key: REDUCER_KEY, saga });
  const {
    isFetchMessageTemplateError: isError = false,
    isLoadingMessageTemplates: isLoading = false,
    messageTemplates = [],
    whatsAppTemplates = [],
    pagination = null,
    whatsAppPagination = null,
  } = useSelector(makeSelectTemplateMenuState());


  // -- State ---------------------------------------------------------------------
  const [inputValue, setInputValue] = useState('');
  const { offsetHeight: searchInputContainerHeight, ref: searchInputContainerRef } = useRefMeasurements();
  const searchInputRef = useRefFocus(true);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimeoutRef = useRef(null);
  const styles = useStyles({ searchInputContainerHeight });

  // The instance of `templates` coming from Redux is frozen by `immer`
  // Thus, create the mutable copy that TemplatesList's sort requires.
  const templates = listWhatsAppTemplatesOnly
    ? [...whatsAppTemplates]
    : [...messageTemplates, ...whatsAppTemplates];

  const isEmptySearchState =
    templates.length === 0 && Boolean(searchQuery) && !isLoading;
  const isEmptyState =
    templates.length === 0 && !searchQuery && !isLoading;

  // -- Effect --------------------------------------------------------------------
  useEffect(() => {
    const cleanup = () => clearTimeout(searchTimeoutRef.current);

    return cleanup;
  }, []);

  useEffect(() => {
    dispatch(
      fetchMessageTemplatesAction(
        templateType || MessageTemplatesType.GENERAL,
        variant,
        sourceAplicantId,
        whatsAppProvider,
        page,
        searchQuery,
      ),
    );
  }, [dispatch, templateType, variant, sourceAplicantId, whatsAppProvider, page, searchQuery]);

  // -- Handler -------------------------------------------------------------------
  const handleChangeInput = event => {
    const value = event.target.value;
    setInputValue(value);

    clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setSearchQuery(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleClearInput = () => {
    clearTimeout(searchTimeoutRef.current);
    setInputValue('');
    setPage(1);
    setSearchQuery('');
    if (searchInputRef && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleClickError = () => {
    setPage(1);
    setSearchQuery('');
    setInputValue('');
    dispatch(
      fetchMessageTemplatesAction(
        MessageTemplatesType.GENERAL,
        variant,
        sourceAplicantId,
        whatsAppProvider,
        1,
        '',
      ),
    );
  };

  // -- UI ------------------------------------------------------------------------
  const sharedProps = { onSelected, searchString: inputValue, variant };
  const templatesListProps = { ...sharedProps, templates };

  const activePagination = listWhatsAppTemplatesOnly ? whatsAppPagination : pagination;
  const showPagination = activePagination?.totalPages > 1;
  return (
    <>
      <Grid
        className={styles.searchInputContainer}
        ref={searchInputContainerRef}
      >
        <SearchInput
          fullWidth
          inputRef={searchInputRef}
          onChange={handleChangeInput}
          onClear={handleClearInput}
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          value={inputValue}
        />
      </Grid>
      <Grid className={styles.listStatesContainer}>
        <Choose>
          <Choose.When condition={isError}>
            <ErrorState onClick={handleClickError} />
          </Choose.When>
          <Choose.When condition={isLoading}>
            <LoadingState message={intl.formatMessage(messages.loading)} />
          </Choose.When>
          <Choose.When condition={isEmptySearchState}>
            <EmptySearchState
              message={intl.formatMessage(messages.emptySearch, {
                searchString: `'${inputValue}'`,
              })}
            />
          </Choose.When>
          <Choose.When condition={isEmptyState}>
            <EmptyState message={intl.formatMessage(messages.empty)} />
          </Choose.When>
          <Choose.Otherwise>
            <Collapse in={!inputValue}>
              <RecentlyUsedTemplatesList {...sharedProps} />
            </Collapse>
            <TemplatesList {...templatesListProps} />
            {
              showPagination && (
                <Pagination
                  compact
                  currentPage={activePagination.currentPage}
                  lastPage={activePagination.totalPages}
                  perPage={activePagination.perPage}
                  setPage={setPage}
                  setPerPage={() => { }}
                  hidePerPage
                />
              )
            }
          </Choose.Otherwise>
        </Choose>
      </Grid>
    </>
  );
};

// == Type ========================================================================
TemplateMenuContent.propTypes = {
  intl: PropTypes.shape({ formatMessage: PropTypes.func.isRequired })
    .isRequired,
  onSelected: PropTypes.func.isRequired,
  templateType: PropTypes.string,
  variant: PropTypes.oneOf([
    MessageTemplatesVariant.EMAIL,
    MessageTemplatesVariant.SMS,
    MessageTemplatesVariant.WHATSAPP,
  ]).isRequired,
  listWhatsAppTemplatesOnly: PropTypes.bool,
  sourceAplicantId: PropTypes.string,
  whatsAppProvider: PropTypes.string,
};

// == Export ======================================================================
export default injectIntl(TemplateMenuContent);
